import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { useTranslation } from "react-i18next";
import {
  useGetMe,
  useGetToday,
  useGetTodayNextAction,
  useCompleteTask,
  usePostponeTask,
  useSuggestNextAction,
  useCreateTask,
  useListInbox,
  useListProjects,
  useListMailAccounts,
  useListMailFolders,
  useListMailMessages,
  useListDelegatedTasks,
  useListDriftAlerts,
  useDismissDriftAlert,
  getListMailFoldersQueryKey,
  getListMailMessagesQueryKey,
  getGetTodayQueryKey,
  getGetTodayNextActionQueryKey,
  getListDelegatedTasksQueryKey,
  getListDriftAlertsQueryKey,
  getGetProjectQueryKey,
  getListProjectsQueryKey,
  getSuggestNextActionQueryKey,
  ApiError,
  type Task,
  type Project,
  type ProjectSummary,
  type InboxItem,
  type ProjectWithoutNextAction,
  type ProcrastinationAlert,
  type TodayNextActionSuggestion,
  type DelegatedTask,
  type DriftAlert,
  type MailAccount,
  type MailFolder,
  type MailMessage,
} from "@workspace/api-client-react";
import { Users } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import LabelBadge from "@/components/label-badge";
import LabelsFilterBar, { LABELS_QUERY_KEY } from "@/components/labels-filter-bar";
import {
  fetchTaskLabels,
  removeTaskLabel,
  type SimpleLabel,
} from "@/lib/labels-api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Circle,
  Clock,
  Zap,
  AlertCircle,
  Lightbulb,
  CalendarClock,
  ExternalLink,
  Loader2,
  Plus,
  Inbox as InboxIcon,
  FolderKanban,
  Mic,
  Mail,
  FileText,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Wand2,
  Pencil,
  RefreshCw,
  LogIn,
  HelpCircle,
  XCircle,
  UserCheck,
  Target,
  MessageSquare,
  GitFork,
  Eye,
  Brain,
  X,
  TrendingDown,
  Archive,
} from "lucide-react";
import { format, isPast, isToday, formatDistanceToNow, getISOWeek } from "date-fns";
import { fr } from "date-fns/locale";
import { enUS } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import QuickCapture from "@/components/quick-capture";
import TaskEditDialog from "@/components/task-edit-dialog";
import AnalyzeNowCard from "@/components/analyze-now-card";
import TaskQuickActions, { DelegationBadge } from "@/components/task-quick-actions";

function getGreetingKey(date: Date): string {
  const h = date.getHours();
  if (h >= 5 && h < 12) return "today_page.greeting_morning";
  if (h >= 12 && h < 18) return "today_page.greeting_afternoon";
  if (h >= 18 && h < 23) return "today_page.greeting_evening";
  return "today_page.greeting_night";
}

function TaskRow({
  task,
  project,
  reason,
  emphasize = false,
  labels,
  inboxItem,
}: {
  task: Task;
  project?: Project | null;
  reason?: string;
  emphasize?: boolean;
  labels?: SimpleLabel[];
  inboxItem?: InboxItem | null;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const completeTask = useCompleteTask();
  const postponeTask = usePostponeTask();
  const [editOpen, setEditOpen] = useState(false);

  const isCompleted = task.status === "done";

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
    if (task.projectId) {
      queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(task.projectId) });
    }
  };

  const removeLabelMutation = useMutation({
    mutationFn: (labelId: string) => removeTaskLabel(task.id, labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-labels"] });
      queryClient.invalidateQueries({ queryKey: LABELS_QUERY_KEY });
    },
    onError: () =>
      toast({ title: "Échec du retrait de l'étiquette", variant: "destructive" }),
  });

  const handleComplete = () => {
    if (isCompleted) return;
    completeTask.mutate(
      { id: task.id },
      {
        onSuccess: () => {
          invalidateAll();
          toast({ title: t("today_page.task_done_toast") });
        },
        onError: () => toast({ title: t("common.complete_error"), variant: "destructive" }),
      },
    );
  };

  const handlePostpone = () => {
    postponeTask.mutate(
      { id: task.id },
      {
        onSuccess: () => {
          invalidateAll();
          toast({ title: t("today_page.task_postponed_toast") });
        },
        onError: () => toast({ title: t("common.postpone_error"), variant: "destructive" }),
      },
    );
  };

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-xl transition-colors border group ${
        emphasize
          ? "border-transparent"
          : "border-transparent hover:border-border/50 hover:bg-secondary/40"
      } ${isCompleted ? "opacity-50" : ""}`}
      data-testid="task-row"
    >
      <button
        type="button"
        onClick={handleComplete}
        disabled={isCompleted || completeTask.isPending}
        className={`mt-1 transition-colors ${
          isCompleted ? "text-primary" : "text-muted-foreground hover:text-primary"
        }`}
        aria-label={isCompleted ? t("today_page.task_done_aria") : t("today_page.task_mark_done_aria")}
      >
        {completeTask.isPending ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isCompleted ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4
            className={`font-medium text-foreground line-clamp-2 ${
              isCompleted ? "line-through" : ""
            }`}
          >
            {task.title}
          </h4>
          {task.priority === "high" && (
            <span className="bg-destructive/10 text-destructive text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm">
              {t("priority.high_short")}
            </span>
          )}
          <DelegationBadge task={task} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          {project && (
            <Link
              href={`/app/projects/${project.id}`}
              className="bg-secondary px-2 py-0.5 rounded-sm text-secondary-foreground font-medium truncate max-w-[200px] hover:text-primary"
            >
              {project.name}
            </Link>
          )}
          {task.estimatedMinutes != null && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {task.estimatedMinutes} min
            </span>
          )}
          {task.dueDate && (
            <span
              className={`flex items-center gap-1 ${
                isPast(new Date(task.dueDate)) && !isToday(new Date(task.dueDate))
                  ? "text-destructive font-medium"
                  : ""
              }`}
            >
              <CalendarClock className="w-3 h-3" />
              {format(new Date(task.dueDate), "d MMM", { locale: dateLocale })}
            </span>
          )}
        </div>
        {labels && labels.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {labels.map((l) => (
              <LabelBadge
                key={l.id}
                name={l.name}
                color={l.color}
                size="xs"
                onRemove={
                  isCompleted || removeLabelMutation.isPending
                    ? undefined
                    : () => removeLabelMutation.mutate(l.id)
                }
              />
            ))}
          </div>
        )}
        {task.inboxItemId && (
          <div className="mt-1.5">
            <Link
              href={`/app/inbox?item=${task.inboxItemId}`}
              className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <InboxIcon className="w-3 h-3" />
              {inboxItem?.sourceType === "email" || inboxItem?.sourceType === "email_forward"
                ? t("app_nav.source_email")
                : inboxItem?.sourceType === "voice"
                  ? t("app_nav.source_voice")
                  : inboxItem?.sourceType === "file"
                    ? t("app_nav.source_file")
                    : inboxItem?.sourceType === "link"
                      ? t("app_nav.source_link")
                      : t("app_nav.captured_from_inbox")}
              {inboxItem?.createdAt && (
                <span className="text-muted-foreground/50">
                  · {format(new Date(inboxItem.createdAt), "d MMM", { locale: dateLocale })}
                </span>
              )}
            </Link>
          </div>
        )}
        {reason && <p className="text-xs text-muted-foreground mt-2 italic">"{reason}"</p>}

        {!isCompleted && (
          // Always-visible action row — touch-friendly. The completion circle
          // on the left already handles "Terminer", so we only expose the
          // secondary actions here (Modifier, Reporter, Ouvrir le projet,
          // + Contexte, Déléguer, Refuser).
          <div className="flex items-center gap-1 mt-3 -ml-2 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground"
              onClick={() => setEditOpen(true)}
              data-testid="button-edit-task"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {t("common.modify")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground"
              onClick={handlePostpone}
              disabled={postponeTask.isPending}
              data-testid="button-postpone-task"
            >
              {postponeTask.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
              )}
              {t("common.postpone_tomorrow")}
            </Button>
            {task.projectId && (
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground"
              >
                <Link href={`/app/projects/${task.projectId}`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {t("common.open_project")}
                </Link>
              </Button>
            )}
            <TaskQuickActions task={task} />
          </div>
        )}
      </div>
      <TaskEditDialog
        task={task}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </div>
  );
}

function StuckProjectCard({ project }: { project: ProjectWithoutNextAction }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const suggestion = useSuggestNextAction(project.id, {
    query: {
      queryKey: getSuggestNextActionQueryKey(project.id),
      enabled,
      staleTime: 60_000,
    },
  });
  const createTask = useCreateTask();

  const handleAccept = () => {
    if (!suggestion.data) return;
    createTask.mutate(
      {
        data: {
          title: suggestion.data.title,
          description: suggestion.data.description ?? null,
          projectId: project.id,
          priority: suggestion.data.priority ?? null,
          estimatedMinutes: suggestion.data.estimatedMinutes ?? null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
          toast({ title: t("today_page.task_added_to_project_toast") });
          setEnabled(false);
        },
        onError: () => toast({ title: t("common.add_error"), variant: "destructive" }),
      },
    );
  };

  return (
    <div className="p-5 rounded-2xl border border-border bg-card flex flex-col">
      <h4 className="font-semibold text-foreground mb-1">{project.name}</h4>
      <p className="text-sm text-muted-foreground mb-4">{project.warning}</p>

      {suggestion.data && enabled && !suggestion.isFetching && (
        <div className="mb-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
          <div className="text-[10px] uppercase font-bold tracking-wider text-primary mb-1">
            {t("common.ai_suggestion")}
          </div>
          <div className="text-sm font-medium">{suggestion.data.title}</div>
          {suggestion.data.reason && (
            <p className="text-xs text-muted-foreground mt-1 italic">{suggestion.data.reason}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEnabled(true);
            void suggestion.refetch();
          }}
          disabled={suggestion.isFetching}
        >
          {suggestion.isFetching ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Lightbulb className="w-4 h-4 mr-2" />
          )}
          {t("common.suggest_action")}
        </Button>
        {suggestion.data && enabled && (
          <Button size="sm" onClick={handleAccept} disabled={createTask.isPending}>
            {createTask.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {t("common.add_to_project")}
          </Button>
        )}
        <Button asChild size="sm" variant="ghost">
          <Link href={`/app/projects/${project.id}`}>
            <ExternalLink className="w-4 h-4 mr-2" /> {t("common.open")}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function SourceIcon({ sourceType }: { sourceType: string }) {
  const className = "w-3.5 h-3.5";
  if (sourceType === "voice") return <Mic className={className} />;
  if (sourceType === "email") return <Mail className={className} />;
  return <FileText className={className} />;
}


function InboxRow({ item }: { item: InboxItem }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const preview =
    (item.aiSummary || item.transcriptText || item.rawText || "").trim() || t("common.empty_content");
  const truncated = preview.length > 90 ? preview.slice(0, 90) + "…" : preview;
  const timeAgo = formatDistanceToNow(new Date(item.createdAt), {
    addSuffix: true,
    locale: dateLocale,
  });

  return (
    <Link
      href="/app/inbox"
      className="flex items-start gap-3 p-3 rounded-xl border border-transparent hover:border-border/50 hover:bg-secondary/40 transition-colors"
      data-testid="inbox-row"
    >
      <div className="mt-0.5 text-muted-foreground">
        <SourceIcon sourceType={item.sourceType} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground line-clamp-2 leading-snug">{truncated}</p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
          <span>{timeAgo}</span>
          {item.status && t(`status.${item.status}`, "") && (
            <>
              <span>·</span>
              <span>{t(`status.${item.status}`, item.status)}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}


function ProjectCard({ project }: { project: ProjectSummary }) {
  const { t, i18n } = useTranslation();
  return (
    <Link
      href={`/app/projects/${project.id}`}
      className="flex flex-col p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all group"
      data-testid="project-card"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
          {project.name}
        </h4>
        {project.priority === "high" && (
          <Badge
            variant="secondary"
            className="text-[10px] uppercase font-bold tracking-wider shrink-0"
          >
            {t("priority.high")}
          </Badge>
        )}
      </div>
      {project.nextAction ? (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3 flex-1">
          → {project.nextAction.title}
        </p>
      ) : (
        <p className="text-xs text-orange-600/80 italic mb-3 flex-1">
          {t("common.no_next_action")}
        </p>
      )}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-auto">
        <span>
          {t(project.openTaskCount === 1 ? "common.open_tasks_one" : "common.open_tasks_other", { count: project.openTaskCount })}
        </span>
        {project.deadline && (
          <span className="flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            {format(new Date(project.deadline), "d MMM", { locale: i18n.language === "en" ? enUS : fr })}
          </span>
        )}
      </div>
    </Link>
  );
}

function FirstAnalysisBanner() {
  const { t } = useTranslation();
  const { user, isLoaded } = useUser();
  const { data: accounts } = useListMailAccounts();
  const accountList = (accounts ?? []) as MailAccount[];
  const { data: inboxData } = useListInbox({});
  const [dismissed, setDismissed] = useState(false);

  // Read once on mount; the banner self-hides as soon as the user navigates
  // away to the wizard, so we don't need a live storage subscription.
  const flag = useMemo(() => {
    if (!isLoaded) return null;
    const key = user?.id
      ? `onenext_first_analysis_done:${user.id}`
      : "onenext_first_analysis_done";
    return localStorage.getItem(key);
  }, [isLoaded, user?.id]);

  // How many inbox items the user actually has after the previous analysis.
  // If a mail account is connected but the triage inbox stays nearly empty,
  // the analysis clearly didn't catch much — most likely the previous run
  // hit the bug where only the cached ~50 messages per folder were scanned.
  // In that case we re-surface the banner (with a "relancer" wording) so
  // the user can re-trigger the now-fixed full sync + analysis flow.
  const inboxCount = (inboxData ?? []).filter(
    (it) => it.status !== "archived",
  ).length;
  const alreadyDone = flag === "true";
  const hasMeaningfulInbox = inboxCount >= 3;

  if (!isLoaded) return null;
  if (dismissed) return null;
  if (accountList.length === 0) return null;
  // Hide as soon as the inbox has meaningful data — whether or not the
  // localStorage flag survived a cache clear.
  if (hasMeaningfulInbox) return null;

  return (
    <div
      className="mb-6 flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4"
      data-testid="banner-first-analysis"
    >
      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Wand2 className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">
          {alreadyDone
            ? t("today_page.mail_rerun")
            : t("today_page.mail_first_run")}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {alreadyDone
            ? t("today_page.mail_rerun_hint")
            : t("today_page.mail_first_hint")}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <Button
          size="sm"
          asChild
          className="rounded-lg"
          data-testid="banner-first-analysis-cta"
        >
          <Link href="/onboarding?step=2">
            {alreadyDone ? t("today_page.mail_rerun_btn") : t("today_page.mail_first_btn")}
          </Link>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="rounded-lg text-muted-foreground"
          onClick={() => setDismissed(true)}
        >
          {t("common.later")}
        </Button>
      </div>
    </div>
  );
}

const ACTION_TYPE_META: Record<
  TodayNextActionSuggestion["actionType"],
  { label: string; icon: React.ReactNode; color: string }
> = {
  reply: {
    label: "Répondre",
    icon: <MessageSquare className="w-4 h-4" />,
    color: "text-blue-500",
  },
  follow_up: {
    label: "Relancer",
    icon: <RefreshCw className="w-4 h-4" />,
    color: "text-indigo-500",
  },
  clarify: {
    label: "Clarifier",
    icon: <Eye className="w-4 h-4" />,
    color: "text-amber-500",
  },
  delegate: {
    label: "Déléguer",
    icon: <GitFork className="w-4 h-4" />,
    color: "text-orange-500",
  },
  execute: {
    label: "Exécuter",
    icon: <Target className="w-4 h-4" />,
    color: "text-emerald-500",
  },
};

function NextBestActionCard({ suggestion }: { suggestion: TodayNextActionSuggestion }) {
  const meta = ACTION_TYPE_META[suggestion.actionType];
  return (
    <div className="flex gap-3 p-4 rounded-xl border bg-card hover:bg-accent/5 transition-colors">
      <div className={`mt-0.5 shrink-0 ${meta.color}`}>{meta.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="font-medium text-sm leading-snug truncate">{suggestion.taskTitle}</p>
          <Badge variant="outline" className={`shrink-0 text-xs ${meta.color} border-current/30`}>
            {meta.label}
          </Badge>
        </div>
        <p className="text-sm text-foreground/90 mb-1">{suggestion.suggestedAction}</p>
        <p className="text-xs text-muted-foreground mb-1">{suggestion.context}</p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="w-3 h-3 shrink-0" />
          <span>{suggestion.bestTime}</span>
          {suggestion.contactHint?.name && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <UserCheck className="w-3 h-3 shrink-0" />
              <span>{suggestion.contactHint.name}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NextBestActionSection() {
  const nextAction = useGetTodayNextAction();

  if (nextAction.isLoading) return null;
  if (!nextAction.data || nextAction.data.length === 0) return null;

  return (
    <section data-testid="section-next-action">
      <SectionHeader
        icon={<Sparkles className="w-4 h-4 text-primary" />}
        title={`Prochaines actions suggérées (${nextAction.data.length})`}
      />
      <div className="space-y-2">
        {nextAction.data.map((s) => (
          <NextBestActionCard key={s.taskId} suggestion={s} />
        ))}
      </div>
    </section>
  );
}

function ProcrastinationAlerts({ alerts }: { alerts: ProcrastinationAlert[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [refuseId, setRefuseId] = useState<string | null>(null);
  const [delegateId, setDelegateId] = useState<string | null>(null);

  const visible = alerts.filter((a) => !dismissed.has(a.task.id));
  if (visible.length === 0) return null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTodayNextActionQueryKey() });
  };

  const handleKeep = (taskId: string) => {
    setDismissed((prev) => new Set(prev).add(taskId));
    toast({ title: "Tâche conservée — bonne chance !" });
  };

  return (
    <section data-testid="section-procrastination">
      <SectionHeader
        icon={<AlertCircle className="w-4 h-4 text-orange-500" />}
        title={`Attention — procrastination (${visible.length})`}
      />
      <div className="space-y-3">
        {visible.map((alert) => (
          <div
            key={alert.task.id}
            className="rounded-2xl border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800/40 p-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-orange-600">
                  {alert.postponeCount}×
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground line-clamp-1">
                  {alert.task.title}
                </p>
                {alert.project && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {alert.project.name}
                  </p>
                )}
                <p className="text-xs text-orange-700 dark:text-orange-400 mt-1 italic">
                  {alert.suggestion}
                </p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-2.5 border-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40"
                    onClick={() => setDelegateId(alert.task.id)}
                  >
                    <UserCheck className="w-3.5 h-3.5 mr-1" />
                    Déléguer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-2.5 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                    onClick={() => setRefuseId(alert.task.id)}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    Refuser
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs px-2.5 text-muted-foreground"
                    onClick={() => handleKeep(alert.task.id)}
                  >
                    Garder
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Refuse dialog */}
      {refuseId && (() => {
        const alert = visible.find((a) => a.task.id === refuseId);
        if (!alert) return null;
        return (
          <RefuseDialog
            taskId={refuseId}
            taskTitle={alert.task.title}
            onClose={() => setRefuseId(null)}
            onSuccess={() => {
              setRefuseId(null);
              setDismissed((prev) => new Set(prev).add(refuseId));
              invalidate();
            }}
          />
        );
      })()}
      {/* Delegate dialog */}
      {delegateId && (() => {
        const alert = visible.find((a) => a.task.id === delegateId);
        if (!alert) return null;
        return (
          <DelegateDialog
            taskId={delegateId}
            taskTitle={alert.task.title}
            onClose={() => setDelegateId(null)}
            onSuccess={() => {
              setDelegateId(null);
              setDismissed((prev) => new Set(prev).add(delegateId));
              invalidate();
            }}
          />
        );
      })()}
    </section>
  );
}

function RefuseDialog({
  taskId,
  taskTitle,
  onClose,
  onSuccess,
}: {
  taskId: string;
  taskTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState("");
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/tasks/${taskId}/refuse`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      if (!res.ok) throw new Error("failed");
    },
    onSuccess: () => {
      toast({ title: "Tâche refusée" });
      onSuccess();
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-base">Refuser la tâche</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{taskTitle}</p>
        <textarea
          className="w-full rounded-xl border border-border bg-background p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Raison du refus (optionnel)…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Refuser
          </Button>
        </div>
      </div>
    </div>
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function DelegateDialog({
  taskId,
  taskTitle,
  onClose,
  onSuccess,
}: {
  taskId: string;
  taskTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [nameOrEmail, setNameOrEmail] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const { toast } = useToast();

  const nameIsEmail = EMAIL_PATTERN.test(nameOrEmail.trim());

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string | null> = {
        assignedToName: nameOrEmail.trim() || null,
        delegationNote: note.trim() || null,
      };
      if (email.trim()) {
        payload.assignedToEmail = email.trim();
      } else if (nameIsEmail) {
        payload.assignedToEmail = nameOrEmail.trim();
      }
      const res = await fetch(`${API_BASE}/api/tasks/${taskId}/delegate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
    },
    onSuccess: () => {
      toast({ title: "Tâche déléguée" });
      onSuccess();
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-base">Déléguer la tâche</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{taskTitle}</p>
        <div className="space-y-2">
          <input
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Déléguer à (nom ou email)…"
            value={nameOrEmail}
            onChange={(e) => setNameOrEmail(e.target.value)}
          />
          {nameOrEmail.trim() && !nameIsEmail && (
            <input
              type="email"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Email de la personne (pour envoyer la délégation)…"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </div>
        <textarea
          className="w-full rounded-xl border border-border bg-background p-3 text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Note de délégation (optionnelle)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {(nameIsEmail || email.trim()) && (
          <p className="text-xs text-muted-foreground">
            Un email de délégation sera envoyé si un compte mail est configuré.
          </p>
        )}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !nameOrEmail.trim()}
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Déléguer
          </Button>
        </div>
      </div>
    </div>
  );
}

function DelegatedTasksSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useListDelegatedTasks();
  const [delegateRelanceId, setDelegateRelanceId] = useState<string | null>(null);
  const tasks = (data ?? []) as DelegatedTask[];

  if (isLoading) return null;
  if (tasks.length === 0) return null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListDelegatedTasksQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListDriftAlertsQueryKey() });
  };

  return (
    <section data-testid="section-delegated">
      <SectionHeader
        icon={<UserCheck className="w-4 h-4 text-purple-500" />}
        title={`En attente de (${tasks.length})`}
      />
      <div className="space-y-2">
        {tasks.map((task) => {
          const relanceThreshold = Number(import.meta.env.VITE_DRIFT_DELEGATED_DAYS ?? 5);
          const needsFollowUp = task.daysSince >= relanceThreshold;
          return (
            <div
              key={task.id}
              className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                needsFollowUp
                  ? "border-purple-200 bg-purple-50/60 dark:bg-purple-950/20 dark:border-purple-800/40"
                  : "border-border/60 bg-card"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                  needsFollowUp
                    ? "bg-purple-100 dark:bg-purple-900/40"
                    : "bg-muted"
                }`}
              >
                <UserCheck
                  className={`w-4 h-4 ${needsFollowUp ? "text-purple-600" : "text-muted-foreground"}`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground line-clamp-1">{task.title}</p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                  {task.assignedToName && (
                    <span className="font-medium text-foreground/70">→ {task.assignedToName}</span>
                  )}
                  {task.delegatedAt && (
                    <span className="flex items-center gap-0.5">
                      <CalendarClock className="w-3 h-3" />
                      {format(new Date(task.delegatedAt), "d MMM")}
                    </span>
                  )}
                  <span
                    className={`font-medium ${needsFollowUp ? "text-purple-600 dark:text-purple-400" : ""}`}
                  >
                    {task.daysSince === 0
                      ? "Aujourd'hui"
                      : `${task.daysSince}j sans réponse`}
                  </span>
                  {task.dueDate && (
                    <span className="flex items-center gap-0.5 text-amber-600">
                      <CalendarClock className="w-3 h-3" />
                      échéance {format(new Date(task.dueDate), "d MMM")}
                    </span>
                  )}
                </div>
                {needsFollowUp && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs px-2.5 border-purple-300 text-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/40"
                      onClick={() => setDelegateRelanceId(task.id)}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Relancer {task.assignedToName?.split(" ")[0] ?? ""}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {delegateRelanceId && (() => {
        const t = tasks.find((x) => x.id === delegateRelanceId);
        if (!t) return null;
        return (
          <RelanceDialog
            taskId={t.id}
            taskTitle={t.title}
            assignedToName={t.assignedToName ?? null}
            daysSince={t.daysSince}
            delegationNote={t.delegationNote ?? null}
            onClose={() => setDelegateRelanceId(null)}
            onSuccess={() => {
              setDelegateRelanceId(null);
              invalidate();
              toast({ title: "Email de relance préparé" });
            }}
          />
        );
      })()}
    </section>
  );
}

function RelanceDialog({
  taskId,
  taskTitle,
  assignedToName,
  daysSince,
  delegationNote,
  onClose,
  onSuccess,
}: {
  taskId: string;
  taskTitle: string;
  assignedToName: string | null;
  daysSince: number;
  delegationNote: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const defaultMessage = [
    `Bonjour ${assignedToName ?? ""},`,
    "",
    `Je voulais prendre de tes nouvelles concernant la tâche "${taskTitle}" que je t'avais confiée il y a ${daysSince} jour${daysSince > 1 ? "s" : ""}.`,
    "",
    "Est-ce que tu as pu avancer dessus ? N'hésite pas à me tenir au courant.",
    "",
    "Merci !",
  ].join("\n");

  const [message, setMessage] = useState(defaultMessage);
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/tasks/${taskId}/followup`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error("failed");
      const body = await res.json() as { ok: boolean; emailSent: boolean; reason: string | null };
      return body;
    },
    onSuccess: (body) => {
      if (body.emailSent) {
        onSuccess();
      } else {
        const reasonMsg =
          body.reason === "no_recipient" ? "Aucun email lié à cette tâche." :
          body.reason === "no_mail_account" ? "Aucun compte email configuré." :
          body.reason === "smtp_error" ? "Erreur d'envoi SMTP." :
          "Email non envoyé.";
        toast({ title: "Relance enregistrée", description: reasonMsg });
        onClose();
      }
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-purple-600" />
          <h3 className="font-semibold text-base">Relancer</h3>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-1">{taskTitle}</p>
        <textarea
          className="w-full rounded-xl border border-border bg-background p-3 text-sm resize-none h-36 focus:outline-none focus:ring-2 focus:ring-primary/30"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Ce message sera envoyé par email si un contact email est lié à cette tâche.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Annuler</Button>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
}

const DRIFT_TYPE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  delegated_no_response: {
    label: "Délégation sans réponse",
    color: "border-purple-200 bg-purple-50 dark:bg-purple-950/20 dark:border-purple-800/40",
    icon: <UserCheck className="w-4 h-4 text-purple-600" />,
  },
  project_inactive: {
    label: "Projet inactif",
    color: "border-orange-200 bg-orange-50/60 dark:bg-orange-950/20 dark:border-orange-800/40",
    icon: <FolderKanban className="w-4 h-4 text-orange-500" />,
  },
  inbox_untreated: {
    label: "Inbox non traité",
    color: "border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-800/40",
    icon: <InboxIcon className="w-4 h-4 text-blue-500" />,
  },
  email_awaiting_reply: {
    label: "Email en attente",
    color: "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/40",
    icon: <Mail className="w-4 h-4 text-amber-500" />,
  },
  document_without_action: {
    label: "Document sans action",
    color: "border-slate-200 bg-slate-50/60 dark:bg-slate-900/30 dark:border-slate-700/40",
    icon: <FileText className="w-4 h-4 text-slate-500" />,
  },
  abandoned_chat: {
    label: "Conversation abandonnée",
    color: "border-green-200 bg-green-50/60 dark:bg-green-950/20 dark:border-green-800/40",
    icon: <MessageSquare className="w-4 h-4 text-green-600" />,
  },
};

function driftAlertLabel(alert: DriftAlert): string {
  const meta = alert.metadata as Record<string, unknown>;
  const days = typeof meta.daysSince === "number" ? meta.daysSince : null;
  const daysTxt = days !== null ? ` · ${days}j sans activité` : "";
  if (alert.type === "delegated_no_response") {
    const name = typeof meta.assignedToName === "string" ? meta.assignedToName : null;
    const task = typeof meta.taskTitle === "string" ? meta.taskTitle : "Tâche";
    return name ? `${task} → ${name}${daysTxt}` : `${task}${daysTxt}`;
  }
  if (alert.type === "project_inactive") {
    const name = typeof meta.projectName === "string" ? meta.projectName : "Projet";
    return `${name}${daysTxt}`;
  }
  if (alert.type === "inbox_untreated") {
    const preview = typeof meta.preview === "string" ? meta.preview : "";
    return (preview || "Item inbox") + daysTxt;
  }
  if (alert.type === "email_awaiting_reply") {
    const subj = typeof meta.subject === "string" ? meta.subject : "Email";
    return `${subj}${daysTxt}`;
  }
  if (alert.type === "document_without_action") {
    const fname = typeof meta.filename === "string" ? meta.filename : "Document";
    return `${fname}${daysTxt}`;
  }
  if (alert.type === "abandoned_chat") {
    const title = typeof meta.title === "string" ? meta.title : "Conversation";
    return `${title}${daysTxt}`;
  }
  return alert.type;
}

type DriftQuickAction = {
  label: string;
  icon: React.ReactNode;
  onClick: (alert: DriftAlert, helpers: DriftActionHelpers) => void;
  variant?: "default" | "outline" | "ghost";
};

type DriftActionHelpers = {
  navigate: (path: string) => void;
  openCreateTask: (projectId?: string, title?: string) => void;
  openRelance: (taskId: string) => void;
  archiveProject: (projectId: string) => void;
  dismiss: (id: string) => void;
};

const DRIFT_QUICK_ACTIONS: Record<string, DriftQuickAction[]> = {
  delegated_no_response: [
    {
      label: "Relancer",
      icon: <RefreshCw className="w-3 h-3" />,
      onClick: (a, h) => h.openRelance(a.referenceId ?? ""),
    },
  ],
  project_inactive: [
    {
      label: "Ajouter une tâche",
      icon: <Plus className="w-3 h-3" />,
      onClick: (a, h) => {
        const meta = a.metadata as Record<string, unknown>;
        const name = typeof meta.projectName === "string" ? meta.projectName : "";
        h.openCreateTask(a.referenceId ?? undefined, `Prochaine étape — ${name}`);
      },
    },
    {
      label: "Archiver",
      icon: <XCircle className="w-3 h-3" />,
      variant: "ghost",
      onClick: (a, h) => h.archiveProject(a.referenceId ?? ""),
    },
  ],
  inbox_untreated: [
    {
      label: "Traiter",
      icon: <ExternalLink className="w-3 h-3" />,
      onClick: (_a, h) => h.navigate("/app/inbox"),
    },
  ],
  email_awaiting_reply: [
    {
      label: "Voir l'email",
      icon: <ExternalLink className="w-3 h-3" />,
      onClick: (a, h) =>
        h.navigate(
          a.referenceId
            ? `/app/mail?id=${encodeURIComponent(a.referenceId)}`
            : "/app/mail",
        ),
    },
  ],
  document_without_action: [
    {
      label: "Créer une tâche",
      icon: <Plus className="w-3 h-3" />,
      onClick: (a, h) => {
        const meta = a.metadata as Record<string, unknown>;
        const fname = typeof meta.filename === "string" ? meta.filename : "document";
        h.openCreateTask(undefined, `Analyser : ${fname}`);
      },
    },
  ],
  abandoned_chat: [
    {
      label: "Reprendre",
      icon: <MessageSquare className="w-3 h-3" />,
      onClick: (_a, h) => h.navigate("/app/chat"),
    },
  ],
};

function DriftAlertsSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useListDriftAlerts();
  const dismiss = useDismissDriftAlert();
  const createTask = useCreateTask();
  const [localDismissed, setLocalDismissed] = useState<Set<string>>(() => new Set());
  const [relanceTaskId, setRelanceTaskId] = useState<string | null>(null);
  const [, navigate] = useLocation();

  const alerts = (data ?? []) as DriftAlert[];
  const visible = alerts.filter((a) => !localDismissed.has(a.id));

  if (isLoading) return null;
  if (visible.length === 0) return null;

  const invalidateDrift = () => {
    queryClient.invalidateQueries({ queryKey: getListDriftAlertsQueryKey() });
  };

  const handleDismiss = (id: string) => {
    setLocalDismissed((prev) => new Set(prev).add(id));
    dismiss.mutate({ id }, { onSuccess: invalidateDrift, onError: () => toast({ title: "Erreur", variant: "destructive" }) });
  };

  const helpers: DriftActionHelpers = {
    navigate: (path) => navigate(path),
    openCreateTask: (projectId, title) => {
      createTask.mutate(
        { data: { title: title ?? "Nouvelle tâche", projectId: projectId ?? undefined } },
        {
          onSuccess: () => {
            toast({ title: "Tâche créée" });
            queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
            queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
          },
          onError: () => toast({ title: "Erreur", variant: "destructive" }),
        },
      );
    },
    openRelance: (taskId) => setRelanceTaskId(taskId),
    archiveProject: async (projectId) => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "archived" }),
        });
        if (!res.ok) throw new Error("failed");
        toast({ title: "Projet archivé" });
        queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
        invalidateDrift();
      } catch {
        toast({ title: "Erreur", variant: "destructive" });
      }
    },
    dismiss: handleDismiss,
  };

  const relanceAlert = relanceTaskId ? visible.find((a) => a.referenceId === relanceTaskId && a.type === "delegated_no_response") : null;
  const relanceMeta = relanceAlert?.metadata as Record<string, unknown> | undefined;

  return (
    <section data-testid="section-drift-alerts">
      <SectionHeader
        icon={<Eye className="w-4 h-4 text-amber-500" />}
        title={`Dérives détectées (${visible.length})`}
      />
      <div className="space-y-2">
        {visible.map((alert) => {
          const typeMeta = DRIFT_TYPE_META[alert.type] ?? {
            label: alert.type,
            color: "border-border bg-card",
            icon: <AlertCircle className="w-4 h-4 text-muted-foreground" />,
          };
          const quickActions = DRIFT_QUICK_ACTIONS[alert.type] ?? [];
          return (
            <div
              key={alert.id}
              className={`flex flex-col gap-2 p-3 rounded-xl border ${typeMeta.color}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">{typeMeta.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-0.5">
                    {typeMeta.label}
                  </p>
                  <p className="text-sm text-foreground line-clamp-2">
                    {driftAlertLabel(alert)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => handleDismiss(alert.id)}
                  disabled={dismiss.isPending}
                  aria-label="Ignorer"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              {quickActions.length > 0 && (
                <div className="flex items-center gap-1.5 pl-7">
                  {quickActions.map((action) => (
                    <Button
                      key={action.label}
                      size="sm"
                      variant={action.variant ?? "outline"}
                      className="h-6 text-xs px-2 gap-1"
                      onClick={() => action.onClick(alert, helpers)}
                    >
                      {action.icon}
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {relanceTaskId && relanceMeta && (
        <RelanceDialog
          taskId={relanceTaskId}
          taskTitle={typeof relanceMeta.taskTitle === "string" ? relanceMeta.taskTitle : ""}
          assignedToName={typeof relanceMeta.assignedToName === "string" ? relanceMeta.assignedToName : null}
          daysSince={typeof relanceMeta.daysSince === "number" ? relanceMeta.daysSince : 0}
          delegationNote={null}
          onClose={() => setRelanceTaskId(null)}
          onSuccess={() => {
            setRelanceTaskId(null);
            handleDismiss(relanceAlert!.id);
            toast({ title: "Relance envoyée" });
          }}
        />
      )}
    </section>
  );
}

function MailsToTreatSection() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { data: accounts } = useListMailAccounts();
  const accountList = (accounts ?? []) as MailAccount[];
  const defaultAccount =
    accountList.find((a) => a.isDefault) ?? accountList[0] ?? null;

  const accountIdForQuery = defaultAccount?.id ?? "";
  const { data: folders } = useListMailFolders(accountIdForQuery, {
    query: {
      queryKey: getListMailFoldersQueryKey(accountIdForQuery),
      enabled: Boolean(defaultAccount),
    },
  });
  const folderList = (folders ?? []) as MailFolder[];
  const inboxFolder = folderList.find((f) => f.role === "inbox");

  const messagesParams = { folderId: inboxFolder?.id, limit: 20 };
  const { data: messages } = useListMailMessages(
    accountIdForQuery,
    messagesParams,
    {
      query: {
        queryKey: getListMailMessagesQueryKey(accountIdForQuery, messagesParams),
        enabled: Boolean(defaultAccount && inboxFolder),
      },
    },
  );
  const messageList = (messages ?? []) as MailMessage[];
  const unread = useMemo(
    () => messageList.filter((m) => m.isUnread).slice(0, 5),
    [messageList],
  );

  if (!defaultAccount) return null;
  if (unread.length === 0) return null;

  return (
    <section data-testid="section-mail-priority">
      <SectionHeader
        icon={<Mail className="w-4 h-4 text-primary" />}
        title={t("today_page.mail_title")}
        count={inboxFolder?.unreadCount ?? unread.length}
        href="/app/mail"
      />
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-2 space-y-1">
          {unread.map((m) => (
            <Link
              key={m.id}
              href="/app/mail"
              className="flex items-start gap-3 p-3 rounded-xl border border-transparent hover:border-border/50 hover:bg-secondary/40 transition-colors"
              data-testid={`today-mail-row`}
            >
              <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground truncate">
                    {m.fromName || m.fromAddress || t("common.unknown")}
                  </span>
                  <span className="text-[11px] text-muted-foreground shrink-0 ml-auto">
                    {m.receivedAt
                      ? formatDistanceToNow(new Date(m.receivedAt), {
                          addSuffix: false,
                          locale: dateLocale,
                        })
                      : ""}
                  </span>
                </div>
                <p className="text-sm text-foreground/80 truncate">
                  {m.subject || t("common.no_subject")}
                </p>
                {m.snippet ? (
                  <p className="text-xs text-muted-foreground truncate">
                    {m.snippet}
                  </p>
                ) : null}
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ── AI Diagnostics types ──────────────────────────────────────────────────

interface OperationalInsight {
  key: string;
  type: "dispersal" | "drift" | "relationship" | "delegated_stall" | "waiting" | "debt";
  severity: "info" | "warning" | "critical";
  title: string;
  message: string;
  action?: {
    type: "view_project" | "view_projects" | "view_contact" | "view_contacts" | "view_inbox" | "create_task" | "archive_project";
    projectId?: string;
    contactId?: string;
    taskTitle?: string;
    label: string;
  };
  actions?: {
    type: "view_project" | "view_projects" | "view_contact" | "view_contacts" | "view_inbox" | "create_task" | "archive_project";
    projectId?: string;
    contactId?: string;
    taskTitle?: string;
    label: string;
  }[];
}

interface RelationalAlert {
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  reason: "silent_important" | "waiting_for_them" | "delegated_stall";
  daysSinceContact: number | null;
  waitingForWhat: string | null;
  linkedProjectName: string | null;
}

interface IntelligenceDiagnostics {
  insights: OperationalInsight[];
  relationalAlerts: RelationalAlert[];
  generatedAt: string;
}

const INSIGHT_TYPE_META: Record<
  OperationalInsight["type"],
  { icon: React.ReactNode; color: string }
> = {
  dispersal: { icon: <Zap className="w-4 h-4" />, color: "text-amber-500" },
  drift: { icon: <TrendingDown className="w-4 h-4" />, color: "text-orange-500" },
  relationship: { icon: <Users className="w-4 h-4" />, color: "text-blue-500" },
  delegated_stall: { icon: <UserCheck className="w-4 h-4" />, color: "text-indigo-500" },
  waiting: { icon: <HelpCircle className="w-4 h-4" />, color: "text-violet-500" },
  debt: { icon: <InboxIcon className="w-4 h-4" />, color: "text-rose-500" },
};

const SEVERITY_BORDER: Record<OperationalInsight["severity"], string> = {
  critical: "border-destructive/40 bg-destructive/5",
  warning: "border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800/40",
  info: "border-border bg-card",
};

const RELATIONAL_REASON_LABELS: Record<RelationalAlert["reason"], string> = {
  silent_important: "Contact silencieux",
  waiting_for_them: "En attente",
  delegated_stall: "Délégué — sans réponse",
};

const RELATIONAL_REASON_COLORS: Record<RelationalAlert["reason"], string> = {
  silent_important: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400",
  waiting_for_them: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/20 dark:text-violet-400",
  delegated_stall: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/20 dark:text-indigo-400",
};

function InsightActionButton({
  action,
  onActionDone,
}: {
  action: NonNullable<OperationalInsight["action"]>;
  onActionDone?: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const handleCreateTask = async () => {
    if (!action.taskTitle) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tasks`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: action.taskTitle }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Tâche créée", description: action.taskTitle });
      void queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
      onActionDone?.();
    } catch {
      toast({ title: "Erreur lors de la création", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveProject = async () => {
    if (!action.projectId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${action.projectId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "paused" }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Projet mis en pause" });
      void queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
      void queryClient.invalidateQueries({ queryKey: ["today-intelligence"] });
      onActionDone?.();
    } catch {
      toast({ title: "Erreur lors de la mise en pause", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (action.type === "view_project" && action.projectId) {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs px-2.5">
        <Link href={`/app/projects/${action.projectId}`}>
          <ExternalLink className="w-3 h-3 mr-1" />
          {action.label}
        </Link>
      </Button>
    );
  }
  if (action.type === "view_projects") {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs px-2.5">
        <Link href="/app/projects">
          <ExternalLink className="w-3 h-3 mr-1" />
          {action.label}
        </Link>
      </Button>
    );
  }
  if (action.type === "view_contact" && action.contactId) {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs px-2.5">
        <Link href={`/app/crm/${action.contactId}`}>
          <ExternalLink className="w-3 h-3 mr-1" />
          {action.label}
        </Link>
      </Button>
    );
  }
  if (action.type === "view_contacts") {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs px-2.5">
        <Link href="/app/crm">
          <ExternalLink className="w-3 h-3 mr-1" />
          {action.label}
        </Link>
      </Button>
    );
  }
  if (action.type === "view_inbox") {
    return (
      <Button asChild size="sm" variant="outline" className="h-7 text-xs px-2.5">
        <Link href="/app/inbox">
          <ExternalLink className="w-3 h-3 mr-1" />
          {action.label}
        </Link>
      </Button>
    );
  }
  if (action.type === "create_task") {
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs px-2.5"
        onClick={handleCreateTask}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : (
          <Plus className="w-3 h-3 mr-1" />
        )}
        {action.label}
      </Button>
    );
  }
  if (action.type === "archive_project" && action.projectId) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs px-2.5"
        onClick={handleArchiveProject}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : (
          <Archive className="w-3 h-3 mr-1" />
        )}
        {action.label}
      </Button>
    );
  }
  return null;
}

function AiDiagnosticsSection() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const storageKey = `ai_diagnostics_collapsed:${user?.id ?? "anon"}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  });

  const dismissedKey = `ai_diagnostics_dismissed:${user?.id ?? "anon"}`;
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(dismissedKey);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const persistDismissed = (next: Set<string>) => {
    setDismissed(next);
    try {
      localStorage.setItem(dismissedKey, JSON.stringify(Array.from(next)));
    } catch {}
  };

  const toggleCollapsed = (val: boolean) => {
    setCollapsed(val);
    try {
      localStorage.setItem(storageKey, String(val));
    } catch {}
  };

  const { data, isLoading, refetch, isFetching } = useQuery<IntelligenceDiagnostics>({
    queryKey: ["today-intelligence"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/today/intelligence`, {
        credentials: "include",
      });
      if (!res.ok) return { insights: [], relationalAlerts: [], generatedAt: new Date().toISOString() };
      return res.json() as Promise<IntelligenceDiagnostics>;
    },
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  const handleRefresh = async () => {
    await fetch(`${API_BASE}/api/today/intelligence?refresh=true`, {
      credentials: "include",
    });
    void queryClient.invalidateQueries({ queryKey: ["today-intelligence"] });
    void refetch();
  };

  const visibleInsights = (data?.insights ?? []).filter(
    (i) => !dismissed.has(i.key),
  );
  const visibleAlerts = data?.relationalAlerts ?? [];

  if (isLoading) return null;
  if (!data || (visibleInsights.length === 0 && visibleAlerts.length === 0)) return null;

  const criticalCount = visibleInsights.filter((i) => i.severity === "critical").length;
  const warningCount = visibleInsights.filter((i) => i.severity === "warning").length;
  const totalBadge = criticalCount + warningCount || visibleInsights.length;

  return (
    <section data-testid="section-ai-diagnostics">
      {/* Header row with collapse toggle */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors"
          onClick={() => toggleCollapsed(!collapsed)}
        >
          <Brain className="w-4 h-4 text-primary" />
          Diagnostics IA
          {totalBadge > 0 && (
            <span className="text-muted-foreground/70 normal-case font-normal tracking-normal">
              · {totalBadge}
            </span>
          )}
          {collapsed ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronUp className="w-3.5 h-3.5" />
          )}
        </button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs px-2.5 text-muted-foreground"
          onClick={handleRefresh}
          disabled={isFetching}
        >
          {isFetching ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
          )}
          Actualiser
        </Button>
      </div>

      {!collapsed && (
        <div className="space-y-2">
          {visibleInsights.map((insight) => {
            const meta = INSIGHT_TYPE_META[insight.type];
            return (
              <div
                key={insight.key}
                className={`flex gap-3 p-4 rounded-xl border ${SEVERITY_BORDER[insight.severity]} relative group`}
              >
                <div className={`mt-0.5 shrink-0 ${meta.color}`}>{meta.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-medium text-sm leading-snug">{insight.title}</p>
                    <button
                      type="button"
                      className="text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0 mt-0.5"
                      onClick={() => persistDismissed(new Set(dismissed).add(insight.key))}
                      aria-label="Masquer cet insight"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                    {insight.message}
                  </p>
                  {/* Render actions array (preferred) or legacy single action */}
                  {((insight.actions ?? []).length > 0 || insight.action) && (
                    <div className="flex flex-wrap gap-1.5">
                      {(insight.actions ?? (insight.action ? [insight.action] : [])).map(
                        (act, idx) => (
                          <InsightActionButton
                            key={idx}
                            action={act}
                            onActionDone={() =>
                              persistDismissed(new Set(dismissed).add(insight.key))
                            }
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Relational alerts */}
          {visibleAlerts.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Relations à ne pas oublier
              </p>
              {visibleAlerts.map((alert) => (
                <Link
                  key={alert.contactId}
                  href={`/app/crm/${alert.contactId}`}
                >
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-card hover:bg-secondary/40 transition-colors cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-primary">
                        {alert.contactName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{alert.contactName}</span>
                        {alert.linkedProjectName && (
                          <span className="text-xs text-muted-foreground">
                            · {alert.linkedProjectName}
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                            RELATIONAL_REASON_COLORS[alert.reason] ?? "bg-muted"
                          }`}
                        >
                          {RELATIONAL_REASON_LABELS[alert.reason] ?? alert.reason}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {alert.waitingForWhat ? (
                          <span className="flex items-center gap-1">
                            <HelpCircle className="w-3 h-3 shrink-0" />
                            {alert.waitingForWhat}
                          </span>
                        ) : alert.daysSinceContact !== null ? (
                          `Dernier contact il y a ${alert.daysSinceContact} jour${alert.daysSinceContact > 1 ? "s" : ""}`
                        ) : (
                          "Aucune interaction enregistrée"
                        )}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

interface ContactFollowUp {
  id: string;
  name: string;
  email: string;
  company: string | null;
  stage: string;
  daysSinceInteraction: number | null;
  lastInteractionAt: string | null;
  waitingForWhat: string | null;
  waitingSince: string | null;
  followUpAt: string | null;
  alertReason: "waiting" | "overdue_followup" | "delegated_no_response" | "no_interaction" | "long_silence";
}

const ALERT_REASON_LABELS: Record<string, string> = {
  waiting: "En attente",
  overdue_followup: "Relance passée",
  delegated_no_response: "Délégué sans réponse",
  no_interaction: "Jamais contacté",
  long_silence: "Silence prolongé",
};

const ALERT_REASON_COLORS: Record<string, string> = {
  waiting: "text-amber-600 bg-amber-50 border-amber-200",
  overdue_followup: "text-red-600 bg-red-50 border-red-200",
  delegated_no_response: "text-purple-600 bg-purple-50 border-purple-200",
  no_interaction: "text-blue-600 bg-blue-50 border-blue-200",
  long_silence: "text-orange-600 bg-orange-50 border-orange-200",
};

function RelationalFollowUps({ followUps }: { followUps: ContactFollowUp[] }) {
  if (followUps.length === 0) return null;

  return (
    <section>
      <SectionHeader
        icon={<Users className="w-4 h-4 text-blue-500" />}
        title="Suivis relationnels"
        count={followUps.length}
        href="/app/crm"
        ctaLabel="Voir tous les contacts"
      />
      <div className="space-y-2">
        {followUps.map((c) => (
          <Link key={c.id} href={`/app/crm/${c.id}`}>
            <div className="flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-card hover:bg-secondary/40 transition-colors cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">
                  {c.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.name}</span>
                  {c.company && (
                    <span className="text-xs text-muted-foreground">· {c.company}</span>
                  )}
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ALERT_REASON_COLORS[c.alertReason] ?? "bg-muted"}`}
                  >
                    {ALERT_REASON_LABELS[c.alertReason] ?? c.alertReason}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {c.waitingForWhat ? (
                    <span className="flex items-center gap-1">
                      <HelpCircle className="w-3 h-3 shrink-0" />
                      {c.waitingForWhat}
                    </span>
                  ) : c.daysSinceInteraction !== null ? (
                    `Dernière interaction il y a ${c.daysSinceInteraction} jour${c.daysSinceInteraction > 1 ? "s" : ""}`
                  ) : (
                    "Aucune interaction enregistrée"
                  )}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({
  icon,
  title,
  count,
  href,
  ctaLabel,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
  href?: string;
  ctaLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
        {icon}
        {title}
        {typeof count === "number" && count > 0 && (
          <span className="text-muted-foreground/70 normal-case font-normal tracking-normal">
            · {count}
          </span>
        )}
      </h2>
      {href && (
        <Link
          href={href}
          className="text-xs text-primary hover:underline flex items-center gap-0.5"
        >
          {ctaLabel ?? t("today_page.cta_see_all")}
          <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

export default function TodayPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { data: me } = useGetMe();
  const today = useGetToday();
  const inbox = useListInbox({});
  const projects = useListProjects({ status: "active" });

  const inboxMap = useMemo(
    () => new Map((inbox.data ?? []).map((i) => [i.id, i])),
    [inbox.data],
  );

  const isLoading = today.isLoading || inbox.isLoading || projects.isLoading;

  const [now, setNow] = useState(() => new Date());
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const greeting = t(getGreetingKey(now));
  const dayName = format(now, "EEEE", { locale: dateLocale });
  const dayNumber = format(now, "d", { locale: dateLocale });
  const monthYear = format(now, "MMMM yyyy", { locale: dateLocale });
  const time = format(now, "HH:mm", { locale: dateLocale });
  const weekNumber = getISOWeek(now);

  // Aggregate "à faire aujourd'hui": top + dueToday + nextActions, deduped, max 5
  const { todayTasks, todayTotalCount, doneTodayCount } = useMemo(() => {
    if (!today.data)
      return {
        todayTasks: [] as Array<{ task: Task; project?: Project | null; reason?: string }>,
        todayTotalCount: 0,
        doneTodayCount: 0,
      };

    const seen = new Set<string>();

    // Count full deduplicated pool (no cap)
    if (today.data.top) seen.add(today.data.top.task.id);
    for (const t of today.data.dueToday) seen.add(t.id);
    for (const rec of today.data.nextActions) seen.add(rec.task.id);
    const todayTotalCount = seen.size;

    // Build displayed list (max 5)
    seen.clear();
    const out: Array<{ task: Task; project?: Project | null; reason?: string }> = [];
    if (today.data.top) {
      seen.add(today.data.top.task.id);
      out.push({
        task: today.data.top.task,
        project: today.data.top.project,
        reason: today.data.top.explanation,
      });
    }
    for (const t of today.data.dueToday) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      out.push({ task: t });
      if (out.length >= 5) break;
    }
    if (out.length < 5) {
      for (const rec of today.data.nextActions) {
        if (seen.has(rec.task.id)) continue;
        seen.add(rec.task.id);
        out.push({ task: rec.task, project: rec.project, reason: rec.explanation });
        if (out.length >= 5) break;
      }
    }
    const doneTodayCount = out.filter((e) => e.task.status === "done").length;
    return { todayTasks: out, todayTotalCount, doneTodayCount };
  }, [today.data]);

  const recentInbox = useMemo(() => {
    const list = inbox.data ?? [];
    return [...list]
      .filter((it) => it.status !== "archived")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [inbox.data]);

  const inboxToTriageCount = useMemo(
    () => (inbox.data ?? []).filter((it) => it.status !== "archived").length,
    [inbox.data],
  );

  const activeProjects = projects.data ?? [];
  const visibleProjects = activeProjects.slice(0, 6);

  // Single batch fetch of label assignments for every task currently on
  // screen — avoids N+1 requests in the row component.
  const visibleTaskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of todayTasks) ids.add(e.task.id);
    for (const t of today.data?.overdue ?? []) ids.add(t.id);
    return Array.from(ids);
  }, [todayTasks, today.data?.overdue]);

  const taskLabelsQuery = useQuery({
    queryKey: ["task-labels", visibleTaskIds.slice().sort().join(",")],
    queryFn: () => fetchTaskLabels(visibleTaskIds),
    enabled: visibleTaskIds.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: followUpsData } = useQuery<ContactFollowUp[]>({
    queryKey: ["crm-follow-ups"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/crm/follow-ups`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json() as Promise<ContactFollowUp[]>;
    },
    staleTime: 60_000,
    retry: 1,
  });
  const followUps = followUpsData ?? [];
  const labelsByTask: Record<string, SimpleLabel[]> =
    taskLabelsQuery.data?.byTaskId ?? {};

  // Filtering: when a label is selected we only render tasks carrying it.
  const filteredTodayTasks = useMemo(() => {
    if (!selectedLabelId) return todayTasks;
    return todayTasks.filter((entry) =>
      (labelsByTask[entry.task.id] ?? []).some((l) => l.id === selectedLabelId),
    );
  }, [todayTasks, labelsByTask, selectedLabelId]);

  const filteredOverdue = useMemo(() => {
    const list = today.data?.overdue ?? [];
    if (!selectedLabelId) return list;
    return list.filter((t) =>
      (labelsByTask[t.id] ?? []).some((l) => l.id === selectedLabelId),
    );
  }, [today.data?.overdue, labelsByTask, selectedLabelId]);

  if (isLoading) {
    return (
      <div className="space-y-8 p-4 md:p-8 max-w-5xl mx-auto pb-24 md:pb-8">
        <div className="space-y-2">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-5 w-56" />
        </div>
        <Skeleton className="h-40 w-full rounded-3xl" />
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-64 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (today.isError || !today.data) {
    const is401 =
      today.error instanceof ApiError && today.error.status === 401;
    return (
      <div className="p-8 text-center space-y-4 max-w-sm mx-auto">
        <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
        <p className="text-destructive font-medium text-sm">
          {is401
            ? t("today_page.error_session")
            : t("common.load_error")}
        </p>
        {is401 ? (
          <Button variant="outline" asChild>
            <Link href="/sign-in">
              <LogIn className="w-4 h-4 mr-2" />
              {t("today_page.sign_in_again")}
            </Link>
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => {
              void today.refetch();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("today_page.retry")}
          </Button>
        )}
      </div>
    );
  }

  const everythingEmpty =
    todayTasks.length === 0 &&
    recentInbox.length === 0 &&
    activeProjects.length === 0 &&
    today.data.projectsWithoutNextAction.length === 0 &&
    followUps.length === 0;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto pb-24 md:pb-8">
      {/* Header */}
      <header className="mb-6 md:mb-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-1">
              {greeting}{me?.name ? `, ${me.name.split(" ")[0]}` : ""}
            </h1>
            <p className="text-muted-foreground" data-testid="today-date">
              <span className="capitalize font-medium text-foreground/80">
                {dayName}
              </span>{" "}
              <span className="font-semibold text-foreground">{dayNumber}</span>{" "}
              <span className="capitalize">{monthYear}</span>
            </p>
          </div>
          <div className="text-right shrink-0 flex flex-col items-end">
            <div
              className="font-semibold text-2xl md:text-3xl text-foreground tabular-nums leading-none"
              data-testid="today-clock"
            >
              {time}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1">
              {i18n.language === "en" ? `Wk. ${weekNumber}` : `Sem. ${weekNumber}`}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            <strong className="text-foreground font-semibold">{todayTotalCount}</strong>{" "}
            {t(todayTotalCount === 1 ? "common.open_tasks_one" : "common.open_tasks_other", { count: todayTotalCount })}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span>
            <strong className="text-foreground font-semibold">{inboxToTriageCount}</strong>{" "}
            {t("status.open").toLowerCase()}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span>
            <strong className="text-foreground font-semibold">{activeProjects.length}</strong>{" "}
            {t("projects_page.status_active").toLowerCase()}
          </span>
        </div>
      </header>

      {/* First-analysis banner (shown once per user, only if a mail account
          is connected and the first onboarding analysis hasn't run yet). */}
      <FirstAnalysisBanner />

      {/* L'analyse mail tourne automatiquement en arrière-plan (scheduler +
          cloud si activé) → plus de bouton "Lancer l'analyse" manuel ici. */}

      {/* Quick capture — bientôt disponible */}
      <div className="mb-6">
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-5 py-4 flex items-center gap-3 text-muted-foreground select-none">
          <span className="text-lg">✦</span>
          <div>
            <p className="text-sm font-medium text-foreground/70">{i18n.language === "en" ? "Quick capture" : "Capture rapide"}</p>
            <p className="text-xs mt-0.5">{i18n.language === "en" ? "Coming soon" : "Fonctionnalité bientôt disponible"}</p>
          </div>
          <span className="ml-auto text-[10px] font-semibold uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2.5 py-0.5">
            {i18n.language === "en" ? "Soon" : "Bientôt"}
          </span>
        </div>
      </div>

      <div className="space-y-10">
        {/* Today tasks — always rendered */}
        <section data-testid="section-today">
          <SectionHeader
            icon={<Zap className="w-4 h-4 text-accent" />}
            title={t("today_page.section_today")}
            count={
              selectedLabelId !== null ? filteredTodayTasks.length : todayTotalCount
            }
            href={todayTotalCount > 5 ? "/app/projects" : undefined}
            ctaLabel={t("today_page.cta_all_tasks")}
          />
          <LabelsFilterBar
            selectedLabelId={selectedLabelId}
            onSelectLabel={setSelectedLabelId}
          />
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-2">
              {filteredTodayTasks.length === 0 ? (
                <div className="p-6 text-center" data-testid="today-empty-state">
                  <CheckCircle2 className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    {selectedLabelId
                      ? t("today_page.empty_today_filter")
                      : t("today_page.empty_today")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedLabelId
                      ? t("today_page.empty_today_filter_hint")
                      : t("today_page.empty_today_hint")}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredTodayTasks.map((entry, idx) => (
                    <TaskRow
                      key={entry.task.id}
                      task={entry.task}
                      project={entry.project}
                      reason={entry.reason}
                      emphasize={idx === 0 && !!today.data?.top && !selectedLabelId}
                      labels={labelsByTask[entry.task.id]}
                      inboxItem={entry.task.inboxItemId ? inboxMap.get(entry.task.inboxItemId) ?? null : null}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily progress bar */}
          {todayTasks.length > 0 && (
            <div className="mt-3 px-1">
              <div className="flex justify-between items-baseline mb-1.5">
                <span className="text-xs text-muted-foreground">
                  {t("today_page.today_progress_label", { done: doneTodayCount, total: todayTasks.length })}
                </span>
                <span className="text-xs font-semibold text-primary tabular-nums">
                  {Math.round((doneTodayCount / todayTasks.length) * 100)}%
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${Math.round((doneTodayCount / todayTasks.length) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </section>

        {/* Overdue (compact) */}
        {filteredOverdue.length > 0 && (
          <section data-testid="section-overdue">
            <SectionHeader
              icon={<AlertCircle className="w-4 h-4 text-destructive" />}
              title={t("today_page.section_late")}
              count={filteredOverdue.length}
            />
            <Card className="rounded-2xl shadow-sm border-destructive/30 bg-destructive/5">
              <CardContent className="p-2 space-y-1">
                {filteredOverdue.slice(0, 4).map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    labels={labelsByTask[task.id]}
                    inboxItem={task.inboxItemId ? inboxMap.get(task.inboxItemId) ?? null : null}
                  />
                ))}
              </CardContent>
            </Card>
          </section>
        )}

        {/* Next best action engine — top 5 urgent tasks with suggested actions */}
        <NextBestActionSection />

        {/* Procrastination alerts — tasks postponed 3+ times */}
        <ProcrastinationAlerts alerts={today.data.procrastinationAlerts ?? []} />

        {/* Delegated tasks — "En attente de" with relance suggestions */}
        <DelegatedTasksSection />

        {/* Drift alerts — projects, delegations, inbox and email drifts */}
        <DriftAlertsSection />

        {/* AI Diagnostics — operational intelligence (dispersal, drift, relational) */}
        <AiDiagnosticsSection />

        {/* Mails à traiter */}
        <MailsToTreatSection />

        {/* Two-column block: Inbox + Projects */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Inbox récente */}
          <section data-testid="section-inbox">
            <SectionHeader
              icon={<InboxIcon className="w-4 h-4 text-primary" />}
              title={t("today_page.section_inbox")}
              count={inboxToTriageCount}
              href="/app/inbox"
              ctaLabel={t("today_page.cta_sort")}
            />
            <Card className="rounded-2xl shadow-sm">
              <CardContent className="p-2">
                {inbox.error ? (
                  <div className="p-6 text-center text-sm text-destructive">
                    {t("common.load_error")}
                  </div>
                ) : recentInbox.length === 0 ? (
                  <div className="p-6 text-center">
                    <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {t("today_page.empty_today_hint")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recentInbox.map((item) => (
                      <InboxRow key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Projets actifs */}
          <section data-testid="section-projects">
            <SectionHeader
              icon={<FolderKanban className="w-4 h-4 text-primary" />}
              title={t("today_page.section_active_projects")}
              count={activeProjects.length}
              href="/app/projects"
            />
            {projects.error ? (
              <Card className="rounded-2xl shadow-sm">
                <CardContent className="p-6 text-center text-sm text-destructive">
                  {t("projects_page.load_error")}
                </CardContent>
              </Card>
            ) : visibleProjects.length === 0 ? (
              <Card className="rounded-2xl shadow-sm">
                <CardContent className="p-6 text-center">
                  <FolderKanban className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    {t("projects_page.empty_all")}
                  </p>
                  <Button asChild size="sm" variant="outline">
                    <Link href="/app/projects">
                      <Plus className="w-4 h-4 mr-1" /> {t("common.new_project")}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {visibleProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Stuck projects */}
        {today.data.projectsWithoutNextAction.length > 0 && (
          <section data-testid="section-stuck">
            <SectionHeader
              icon={<AlertCircle className="w-4 h-4 text-orange-500" />}
              title={t("today_page.section_blocked_projects")}
              count={today.data.projectsWithoutNextAction.length}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              {today.data.projectsWithoutNextAction.map((project) => (
                <StuckProjectCard key={project.id} project={project} />
              ))}
            </div>
          </section>
        )}

        {/* Relationship follow-ups */}
        <RelationalFollowUps followUps={followUps} />

        {/* Empty state */}
        {everythingEmpty && (
          <div className="text-center p-12 border border-border/50 border-dashed rounded-3xl bg-secondary/20">
            <CheckCircle2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">
              {t("today_page.empty_today")}
            </h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              {t("today_page.empty_today_hint")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
